import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Chip,
  TextField,
} from "@mui/material";
import { LoadingSpinner } from '../../../components/shared/LoadingSpinner';
import { CloseIcon, ExpandMoreIcon, EditIcon } from '../../../theme/icons';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';

const WorkloadModal = ({
  open,
  onClose,
  accountNumber,
  workloads,
  isGettingWorkloads,
  getWorkloadsError,
  getWorkloadsMessage,
  getOrgAccountWorkloads,
  updateWorkload,
  isUpdatingWorkload,
  updateWorkloadError,
  updateWorkloadMessage,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    if (open && accountNumber) {
      getOrgAccountWorkloads(accountNumber);
    } else if (!open) {
      // Clear messages when modal closes
      setExpandedRows({});
      setEditingRows({});
      setEditValues({});
    }
  }, [open, accountNumber, getOrgAccountWorkloads]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (updateWorkloadMessage) {
      const timer = setTimeout(() => {
        // You might want to dispatch a clear message action here
        // For now, we'll just let it persist
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [updateWorkloadMessage]);

  const toggleRowExpansion = (workloadId) => {
    setExpandedRows(prev => ({
      ...prev,
      [workloadId]: !prev[workloadId]
    }));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "IN PROGRESS":
        return "warning";
      default:
        return "error";
    }
  };

  const handleEditClick = (workloadId, currentValue) => {
    setEditingRows(prev => ({ ...prev, [workloadId]: true }));
    setEditValues(prev => ({ ...prev, [workloadId]: currentValue }));
  };

  const handleCancelEdit = (workloadId) => {
    setEditingRows(prev => ({ ...prev, [workloadId]: false }));
    setEditValues(prev => ({ ...prev, [workloadId]: undefined }));
  };

  const handleSaveEdit = (workloadId, originalValue) => {
    const newValue = parseInt(editValues[workloadId]);
    if (newValue !== originalValue && newValue > 0) {
      updateWorkload(accountNumber, workloadId, newValue);
    }
    setEditingRows(prev => ({ ...prev, [workloadId]: false }));
    setEditValues(prev => ({ ...prev, [workloadId]: undefined }));
  };

  const handleInputChange = (workloadId, value) => {
    setEditValues(prev => ({ ...prev, [workloadId]: value }));
  };

  const handleIncrement = (workloadId, currentValue) => {
    const numValue = parseInt(currentValue) || 1;
    const newValue = numValue + 1;
    setEditValues(prev => ({ ...prev, [workloadId]: newValue }));
  };

  const handleDecrement = (workloadId, currentValue) => {
    const numValue = parseInt(currentValue) || 1;
    const newValue = Math.max(1, numValue - 1);
    setEditValues(prev => ({ ...prev, [workloadId]: newValue }));
  };

  // Expandable row component for detailed information
  const ExpandableRow = ({ workload }) => {
    const isExpanded = expandedRows[workload["Workload ID"]];
    const isEditing = editingRows[workload["Workload ID"]];
    const currentEditValue = editValues[workload["Workload ID"]];
    const originalValue = workload["automationRunLimitPerDay"];
    const hasChanged = currentEditValue && parseInt(currentEditValue) !== originalValue;
    
    return (
      <>
        <TableRow>
          <TableCell>
            <IconButton
              size="small"
              onClick={() => toggleRowExpansion(workload["Workload ID"])}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {workload["Workload ID"]}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {workload["Assessment Name"]}
            </Typography>
          </TableCell>
          <TableCell>
            {isEditing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white' }}>
                  <TextField
                    size="small"
                    type="number"
                    value={currentEditValue}
                    onChange={(e) => handleInputChange(workload["Workload ID"], e.target.value)}
                    onFocus={(e) => e.target.select()}
                    inputProps={{ 
                      min: 1,
                      step: 1,
                      style: { 
                        width: '50px', 
                        textAlign: 'center',
                        padding: '6px 4px',
                        fontSize: '14px',
                        border: 'none',
                        outline: 'none'
                      } 
                    }}
                    variant="standard"
                    sx={{ 
                      '& .MuiInput-underline:before': {
                        borderBottom: 'none',
                      },
                      '& .MuiInput-underline:after': {
                        borderBottom: 'none',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none',
                      },
                      '& .MuiInputBase-input': {
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                          '-webkit-appearance': 'none',
                          margin: 0,
                        },
                        '&[type=number]': {
                          '-moz-appearance': 'textfield',
                        },
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', borderLeft: '1px solid #ccc', height: '32px' }}>
                    <Box
                      sx={{
                        width: '20px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderBottom: '1px solid #ccc',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        }
                      }}
                      onClick={() => handleIncrement(workload["Workload ID"], currentEditValue)}
                    >
                      <Typography sx={{ fontSize: '10px', lineHeight: 1 }}>▲</Typography>
                    </Box>
                    <Box
                      sx={{
                        width: '20px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        }
                      }}
                      onClick={() => handleDecrement(workload["Workload ID"], currentEditValue)}
                    >
                      <Typography sx={{ fontSize: '10px', lineHeight: 1 }}>▼</Typography>
                    </Box>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  color="success"
                  disabled={!hasChanged || isUpdatingWorkload}
                  onClick={() => handleSaveEdit(workload["Workload ID"], originalValue)}
                  sx={{ 
                    width: '32px',
                    height: '32px',
                    backgroundColor: hasChanged ? '#4caf50' : '#e0e0e0',
                    color: hasChanged ? 'white' : '#9e9e9e',
                    '&:hover': {
                      backgroundColor: hasChanged ? '#45a049' : '#d0d0d0',
                    }
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  disabled={isUpdatingWorkload}
                  onClick={() => handleCancelEdit(workload["Workload ID"])}
                  sx={{ 
                    width: '32px',
                    height: '32px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#da190b',
                    }
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {workload["automationRunLimitPerDay"]}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleEditClick(workload["Workload ID"], originalValue)}
                  disabled={isUpdatingWorkload}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Assessment Details
                </Typography>
                <Table size="small" aria-label="assessment details">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell>
                        <Chip
                          label={workload["Status"]}
                          size="small"
                          color={getStatusColor(workload["Status"])}
                        />
                      </TableCell>
                      <TableCell><strong>Account Number</strong></TableCell>
                      <TableCell>{workload["Account Number"]}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Account Code</strong></TableCell>
                      <TableCell>{workload["Account Code"]}</TableCell>
                      <TableCell><strong>Primary Region</strong></TableCell>
                      <TableCell>{workload["PrimaryRegion"]}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Assessment Owner</strong></TableCell>
                      <TableCell>{workload["Assessment Owner"]}</TableCell>
                      <TableCell><strong>Assessment Approver</strong></TableCell>
                      <TableCell>{workload["Assessment Approver"]}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Framework(s)</strong></TableCell>
                      <TableCell>{workload["Framework(s)"]}</TableCell>
                      <TableCell><strong>TA Enabled</strong></TableCell>
                      <TableCell>
                        <Chip
                          label={workload["is_ta_enabled"] ? "Yes" : "No"}
                          size="small"
                          color={workload["is_ta_enabled"] ? "success" : "error"}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Created Date</strong></TableCell>
                      <TableCell>{formatDate(workload["createdDate"])}</TableCell>
                      <TableCell><strong>Last Modified</strong></TableCell>
                      <TableCell>{formatDate(workload["lastModifiedDate"])}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Created By</strong></TableCell>
                      <TableCell>{workload["createdBy"]}</TableCell>
                      <TableCell><strong>Last Modified By</strong></TableCell>
                      <TableCell>{workload["lastModifiedBy"]}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Workload ARN</strong></TableCell>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {workload["WorkloadArn"]}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          minHeight: "80vh",
          maxHeight: "95vh",
          width: "95vw",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          Workloads for Account: {accountNumber}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading Spinner */}
        {isGettingWorkloads && <LoadingSpinner />}

        {/* Error Message */}
        {getWorkloadsError && !isGettingWorkloads && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading workloads: {getWorkloadsError.message || JSON.stringify(getWorkloadsError)}
          </Alert>
        )}

        {/* Update Success Message */}
        {updateWorkloadMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {updateWorkloadMessage}
          </Alert>
        )}

        {/* Update Error Message */}
        {updateWorkloadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error updating workload: {updateWorkloadError.message || JSON.stringify(updateWorkloadError)}
          </Alert>
        )}

        {/* Update Loading Spinner */}
        {isUpdatingWorkload && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <LoadingSpinner />
          </Box>
        )}

        {/* Workloads Table */}
        {workloads && !isGettingWorkloads && (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader aria-label="workloads table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>Expand</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Workload ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Assessment Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Daily Run Limit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workloads.map((workload) => (
                  <ExpandableRow key={workload["Workload ID"]} workload={workload} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* No Data Message */}
        {!workloads && !isGettingWorkloads && !getWorkloadsError && (
          <Typography variant="body1" color="textSecondary" align="center">
            No workload data available for this account.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkloadModal;
